class InspirationGalleryAnnotations {
	private container: HTMLElement;
	private img: HTMLImageElement;
	private parent: HTMLElement;

	constructor(container: HTMLElement) {
		this.container = container;
		this.img = container.previousElementSibling as HTMLImageElement;
		this.parent = container.parentElement as HTMLElement;

		this.container.classList.add('inspirationGalleryAnnotations--isSetup');

		if (this.isImageLoaded()) {
			this.size();
		} else {
			this.img.addEventListener('load', () => this.size());
		}

		this.setupEventListeners();
	}

	public isImageLoaded(): boolean {
		return this.img.complete && this.img.naturalWidth !== 0;
	}

	public size(): void {
		if (!this.isImageLoaded()) return;

		// Ensure the annotation is positioned absolutely
		this.container.style.position = 'absolute';

		// Calculate the image's natural dimensions
		const imgAspectRatio = this.img.naturalWidth / this.img.naturalHeight;
		const parentAspectRatio = this.parent.clientWidth / this.parent.clientHeight;

		let imgWidth: number, imgHeight: number;

		if (imgAspectRatio > parentAspectRatio) {
			// Image is wider than parent
			imgHeight = this.parent.clientHeight;
			imgWidth = imgHeight * imgAspectRatio;
		} else {
			// Image is taller than parent
			imgWidth = this.parent.clientWidth;
			imgHeight = imgWidth / imgAspectRatio;
		}

		const leftOffset = (this.parent.clientWidth - imgWidth) / 2;
		const topOffset = (this.parent.clientHeight - imgHeight) / 2;

		// Set the annotation's position
		this.container.style.left = `${leftOffset}px`;
		this.container.style.right = `${leftOffset}px`;
		this.container.style.top = `${topOffset}px`;
		this.container.style.bottom = `${topOffset}px`;
	}

	private setupEventListeners(): void {
		this.container.addEventListener('click', this.handleClick.bind(this));
		this.container.addEventListener('focusout', this.handleFocusOut.bind(this));
	}

	private handleClick(event: Event): void {
		const dot = event.target as Element;
		if (!dot.matches('.inspirationGalleryAnnotation__icon')) return;
	
		event.preventDefault();
	
		const annotation = dot.parentNode as Element;
		annotation.classList.add('inspirationGalleryAnnotation--active');
	
		const parentContainerRect = this.parent.getBoundingClientRect();
		const detail = annotation.querySelector('.inspirationGalleryAnnotation__detail') as HTMLElement;
		const dotRect = (dot as HTMLElement).getBoundingClientRect();
	
		// Calculate the available space in each direction
		const dotHalfWidth = dotRect.width / 2;
		const dotHalfHeight = dotRect.height / 2;
		const spaceAbove = (dotRect.top + dotHalfHeight) - parentContainerRect.top;
		const spaceBelow = parentContainerRect.bottom - (dotRect.bottom - dotHalfHeight);
		const spaceLeft = (dotRect.left + dotHalfWidth) - parentContainerRect.left;
		const spaceRight = parentContainerRect.right - (dotRect.right - dotHalfWidth);
	
		// Determine vertical position
		let translateY: number;
		if (spaceBelow >= detail.offsetHeight) {
			// Position below the annotation
			translateY = 0;
		} else if (spaceAbove >= detail.offsetHeight) {
			// Position above the annotation
			translateY = -detail.offsetHeight;
		} else {
			// Position touching the bottom of the annotation circle
			translateY = 0;
		}
	
		// Determine horizontal position
		let translateX: number;
		const halfDetailWidth = detail.offsetWidth / 2;
		const minEdgeDistance = 20; // Minimum distance from edge
	
		// Start with centered position
		translateX = 0 - halfDetailWidth;
	
		// Check if centered position is too close to left edge
		if (spaceLeft - minEdgeDistance < halfDetailWidth) {
			translateX = 0 - spaceLeft + minEdgeDistance;
		}
		// Check if centered position is too close to right edge
		else if (spaceRight - minEdgeDistance < halfDetailWidth) {
			translateX = 0 - halfDetailWidth - (halfDetailWidth - (spaceRight - minEdgeDistance));
		}
	
		// If there's not enough space on either side, center it
		if (spaceLeft + spaceRight + dotRect.width < detail.offsetWidth + 2 * minEdgeDistance) {
			translateX = dotHalfWidth - halfDetailWidth;
		}
	
		// Apply the transform
		detail.style.transform = `translate(${translateX}px, ${translateY}px)`;
	}

	private handleFocusOut(event: FocusEvent): void {
		const target = event.target as Element;
		if (!target.matches('.inspirationGalleryAnnotation__icon')) return;

		const annotation = target.parentNode as Element;
		annotation.classList.remove('inspirationGalleryAnnotation--active');
	}

	private closest(element: Element, selector: string): Element | null {
		while (element && element !== document.body) {
			if (element.matches(selector)) return element;
			element = element.parentElement!;
		}
		return null;
	}
}

class InspirationGalleryAnnotationsManager {
	private static instance: InspirationGalleryAnnotationsManager;
	private annotations: InspirationGalleryAnnotations[] = [];

	private constructor() {
		this.initializeExistingAnnotations();
		this.setupMutationObserver();
		this.setupResizeListener();
	}

	public static getInstance(): InspirationGalleryAnnotationsManager {
		if (!InspirationGalleryAnnotationsManager.instance) {
			InspirationGalleryAnnotationsManager.instance = new InspirationGalleryAnnotationsManager();
		}
		return InspirationGalleryAnnotationsManager.instance;
	}

	private initializeExistingAnnotations(): void {
		const existingAnnotations = document.querySelectorAll('.inspirationGalleryAnnotations:not(.inspirationGalleryAnnotations--isSetup)');
		existingAnnotations.forEach(annotation => {
			this.create(annotation as HTMLElement);
		});
	}

	private setupMutationObserver(): void {
		const observer = new MutationObserver((mutations) => {
			mutations.forEach((mutation) => {
				if (mutation.type === 'childList') {
					this.checkForNewAnnotations(mutation.target as HTMLElement);
				}
			});
		});

		observer.observe(document.body, { childList: true, subtree: true });
	}

	private checkForNewAnnotations(node: HTMLElement): void {
		const childAnnotations = node.querySelectorAll('.inspirationGalleryAnnotations:not(.inspirationGalleryAnnotations--isSetup)');
		childAnnotations.forEach(annotation => {
			this.create(annotation as HTMLElement);
		});
	}

	private create(container: HTMLElement): void {
		const annotation = new InspirationGalleryAnnotations(container);
		this.annotations.push(annotation);
	}

	private setupResizeListener(): void {
		window.addEventListener('resize', () => {
			this.annotations.forEach((annotation) => {
				if (annotation.isImageLoaded()) {
					requestAnimationFrame(() => annotation.size()); // Delay the call so the browser has a frame to relayout elements we depend on.
				}
			});
		});
	}
}

// Initialize the InspirationGalleryAnnotationsManager
InspirationGalleryAnnotationsManager.getInstance();